<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import dayjs from 'dayjs';
import { computed, defineAsyncComponent, onMounted } from 'vue';
import { useModal } from 'vue-final-modal';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '~/auth/stores/auth.store';
import { useTransmittalActions } from '~/dms/composables/transmittal-actions.composable.js';
import { useDMSSettingsStore } from '~/dms/store/dms-settings.store';
import { useTransmittalsStore } from '~/dms/store/transmittals.store';

// Modal
import HawkLoader from '~/common/components/atoms/hawk-loader.vue';
import TransmittalReviewStatus from '~/dms/components/transmittals/transmittal-review-status.vue';

const props = defineProps({
  form$: {
    required: true,
  },
  transmittal: { type: Object },
  new_documents: { type: Array },
  classes: {
    type: String,
    required: false,
    default: () => 'col-span-12 -mx-4 px-4',
  },
  is_public_page: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Object,
    default: () => {
      return {
        is_date_disabled: false,
        can_add_documents: true,
      };
    },
  },
  read_only: {
    type: Boolean,
    default: false,
  },
  canDelete: {
    type: Boolean,
    default: true,
  },
  visible_columns: {
    type: Array,
    default: () => {
      return [
        'name',
        'versions',
        'due_date',
        'comments_out',
        'delete',
      ];
    },
  },
});

const emits = defineEmits(['delete', 'closeAttachment', 'rowClick', 'updateDocument']);

const DocumentBrowsePopup = defineAsyncComponent({
  loader: () => import('~/dms/components/atoms/document-browse-popup.vue'),
  loadingComponent: HawkLoader,
});

const $t = inject('$t');

const router = useRouter();
const route = useRoute();

const transmittal_store = useTransmittalsStore();
const dms_settings_store = useDMSSettingsStore('dms-documents-settings');
const authStore = useAuthStore();

const state = reactive({
  selected_documents: [],
  table_instance: null,
  bulk_options: [],
  is_fetching_annotations: false,
});

const members = (props.transmittal?.members || []).map(member => ({ ...member, role: member.access || member.role }));

const has_submitter = !!members.find(member => member.role === 'submitter');

const { dropdown_items, clearBulkSelection } = useTransmittalActions(state);

const width_divider = props.read_only ? 20 : 8;

const search = ref(route.query.search || '');

// Get active custom fields for documents
const custom_fields = computed(() => dms_settings_store.custom_fields?.filter(field => field.active) || []);

// Custom field columns - hidden by default
const custom_field_columns = computed(() => custom_fields.value.map(custom_field => ({
  header: custom_field.label,
  accessorKey: custom_field.uid,
  id: custom_field.uid,
  size: 150,
  enableResizing: false,
  visible: false, // Hide by default, user must explicitly enable
})));

// All available column definitions
const all_columns = computed(() => [{
  accessorKey: 'select',
  header: '',
  id: 'select',
  size: 3,
  enableSorting: false,
  enableResizing: false,
  show_on_hover: true,
}, {
  header: $t('Documents'),
  accessorKey: 'name',
  id: 'name',
  size: window.innerWidth / width_divider,
  enableResizing: false,
  static: true, // Mandatory field
}, {
  header: $t('Revisions'),
  accessorKey: 'versions',
  id: 'versions',
  size: 3,
  enableResizing: false,
}, {
  header: props?.options?.due_date_header || $t('Due date'),
  accessorKey: 'due_date',
  id: 'due_date',
  size: 138,
  enableResizing: false,
}, ...(has_submitter
  ? [{
      header: $t('Submitted date'),
      accessorKey: 'submitted_at',
      id: 'submitted_at',
      size: 138,
      enableResizing: false,
    }]
  : []), {
  header: $t('Comments in'),
  accessorKey: 'comments_in',
  id: 'comments_in',
  size: window.innerWidth / width_divider + 60,
  enableResizing: false,
}, {
  header: $t('Comments out'),
  accessorKey: 'comments_out',
  id: 'comments_out',
  size: window.innerWidth / width_divider + 60,
  enableResizing: false,
}, {
  header: $t('Status'),
  accessorKey: 'status',
  id: 'status',
  size: window.innerWidth / width_divider,
  enableResizing: false,
  static: true, // Mandatory field
}, ...custom_field_columns.value, // Add custom field columns
{
  header: '',
  accessorKey: 'locked',
  id: 'locked',
  size: '3',
  enableResizing: false,
  static: true, // Don't show in column configuration
}, {
  header: '',
  accessorKey: 'delete',
  id: 'delete',
  size: '3',
  enableResizing: false,
  static: true, // Don't show in column configuration
}, {
  header: '',
  accessorKey: 'public_uploads',
  id: 'public_uploads',
  size: '3',
  enableResizing: false,
  static: true, // Don't show in column configuration
}, {
  accessorKey: 'context_menu',
  header: '',
  id: 'context_menu',
  size: '3',
  show_on_hover: 'true',
  static: true, // Don't show in column configuration
}]);

// Filter columns based on visible_columns prop
const columns = computed(() => {
  // Static columns that are always shown (not configurable)
  const always_visible_columns = ['select', 'context_menu', 'locked'];
  return all_columns.value.filter(column =>
    always_visible_columns.includes(column.id)
    || props.visible_columns.includes(column.id)
    || custom_field_columns.value.some(cf => cf.id === column.id), // Include all custom fields
  );
});

const form$ = computed(() => props.form$);
const table_data = computed(() => props.new_documents || Object.values(props.transmittal?.documents || []).filter(doc => doc.uid));
const show_search = table_data.value.length > 5;

// Transform documents data to include custom field values
const documents_with_custom_fields = computed(() => {
  return table_data.value.map((document) => {
    return {
      ...document,
      ...(Array.isArray(document?.field_values)
        ? document.field_values.reduce((acc, field) => {
          acc[field.field] = field.value;
          return acc;
        }, {})
        : {}),
    };
  });
});

const searched_table_data = computed(() => {
  if (search.value && show_search) {
    const lowerCaseSearch = search.value.toLowerCase();
    return documents_with_custom_fields.value.filter(data => data.name?.toLowerCase().includes(lowerCaseSearch) || data.number?.toLowerCase().includes(lowerCaseSearch));
  }
  return documents_with_custom_fields.value;
});
const transmittal = computed(() => transmittal_store.transmittal);
const document_status_map = computed(() => transmittal.value?.status_set);

// Default columns - use visible_columns prop to determine which columns to show
const default_columns = computed(() => {
  const always_visible_columns = ['select', 'context_menu', 'locked'];
  return columns.value.filter(column =>
    always_visible_columns.includes(column.id) || props.visible_columns.includes(column.id),
  );
});

// Check if custom view configuration should be enabled
// Only enable when: not public page AND transmittal exists (detail page)
const is_custom_view_enabled = computed(() =>
  !props.is_public_page && (route.params.transmittal_uid || props.transmittal?.uid),
);

// View configuration for column customization
const custom_view_configuration = computed(() => {
  if (!is_custom_view_enabled.value)
    return null;

  return {
    service: 'dms',
    resource_type: 'transmittals',
    feature: 'transmittal_view',
    name: `${authStore.current_organization.uid}_${route.params.transmittal_uid || props.transmittal?.uid}`,
    store_key: 'dms_transmittal_documents_list_view',
    modify_permissions: authStore.check_permission('configure_dms_settings', route.params.asset_id),
  };
});

// document's modal
const browse_popup = useModal({
  component: DocumentBrowsePopup,
});

function openBrowsePopup() {
  browse_popup.patchOptions({
    attrs: {
      title: $t('Select documents from Files'),
      is_transmittal: true,
      select_folder: true,
      action_label: $t('Upload Documents'),
      on_submit: ({ selected_documents = [] }) => {
        addDocuments({ selected_documents, callback: browse_popup.close });
      },
      onClose() {
        browse_popup.close();
      },
    },
  });
  browse_popup.open();
}

function addDocuments({ selected_documents, callback }) {
  // eslint-disable-next-line array-callback-return
  const data = selected_documents.map((document) => {
    const { uid, name, number, thumbnail_large, thumbnail_small, thumbnail_xsmall, service, active_transmittal } = document;
    if (!form$.value.data.documents[document.uid]) {
      return {
        uid,
        name,
        number,
        thumbnail_large,
        thumbnail_small,
        thumbnail_xsmall,
        due_date: new Date(),
        service_object: service,
        active_transmittal,
      };
    }
  }).filter(file => file);
  emits('updateDocument', [...props.new_documents, ...data]);
  if (props?.form$?.elements$?.documents)
    props.form$.elements$.documents.validate();
  callback();
}

function commonOptionsForDocument() {
  const common_keys = {};
  common_keys.bulk_download = state.selected_documents.filter(doc => doc.presigned_url).length;
  if (common_keys.bulk_download === 0)
    delete common_keys.bulk_download;

  for (const obj of state.selected_documents) {
    for (const key in obj.actions) {
      if (obj.actions[key] === true) {
        if (common_keys[key])
          common_keys[key]++;
        else
          common_keys[key] = 1;
      }
    }
  }

  const common_options = Object.keys(common_keys).filter(key => common_keys[key] === state.selected_documents.length);
  return dropdown_items(null, common_options);
}

function handleRowSelection(e) {
  state.selected_documents = e.map(forms_list_row => forms_list_row.original);
  state.bulk_options = commonOptionsForDocument(); // find common options
}

function formatTable(cell) {
  if (cell?.row?.original?.locked)
    return { backgroundColor: '#F2F4F7' };
  return {};
}

function checkPermission(document, type) {
  if (transmittal.value?.uid)
    return document?.actions?.[type] || false;

  return true;
}

// Fetch custom fields on mount
onMounted(async () => {
  if (!props.is_public_page)
    await dms_settings_store.set_custom_fields({ query: { allowed_in: 'documents' } });
});
</script>

<template>
  <div :class="classes">
    <div class="flex items-center gap-4 mb-2">
      <div class="text-sm font-semibold">
        {{ $t('Documents') }} <span v-if="searched_table_data.length"> ({{ searched_table_data.length }})</span>
      </div>
      <div v-if="show_search" class="relative">
        <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
          <IconHawkSearch class="text-gray-600 size-4" />
        </div>
        <input
          v-model="search"
          name="input"
          type="text"
          autocomplete="off"
          :placeholder="$t('Search Document')"
          class="block w-full py-1 text-gray-900 pl-8 placeholder:text-gray-500 text-sm sm:leading-6 rounded-lg border border-gray-300 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 focus:outline-none"
        >
      </div>
    </div>
    <TransmittalDetailDocumentBulkActions
      v-if="state.bulk_options?.length > 0"
      :selected_items="state.selected_documents"
      :bulk_action_options="state.bulk_options"
      class="mb-4"
      @clear="clearBulkSelection"
    />
    <hawk-table
      :columns="columns"
      :default_columns="default_columns"
      :is_vueform_element_enabled="true"
      :data="searched_table_data"
      class="!px-0"
      :is_gapless="true"
      :show_menu_header="true"
      :disable_resize="true"
      :pagination_config=" { totalRows: table_data?.length, pageSize: 25 }"
      scroll_to_top_on_pagination
      :format-table="formatTable"
      :custom_view_configuration="is_custom_view_enabled ? custom_view_configuration : undefined"
      :header_context_menu_config="is_custom_view_enabled && custom_view_configuration.modify_permissions ? {
        is_menu_fixed: true,
        column_settings: true,
        max_selected_items: 6,
        min_selected_items: 2,
      } : {
        is_menu_fixed: true,
        column_settings: false,
      }"
      @table-instance="state.table_instance = $event"
      @select-row="handleRowSelection"
      @row-clicked="emits('rowClick', $event)"
    >
      <template #due_dateHeader>
        {{ options?.due_date_header || $t('Due date') }}
      </template>
      <template #name="slotProps">
        <div class="flex items-center gap-4">
          <div>
            <div class="text-xs text-gray-600 font-regular">
              {{ slotProps.data.row.original.number }}
            </div>
            <div class="text-sm font-medium text-gray-800">
              {{ slotProps.data.row.original.name }}
              <hawk-badge v-if="slotProps.data.row.original.unread" color="blue" type="light">
                {{ $t('New') }}
              </hawk-badge>
            </div>
          </div>
          <div
            v-if="slotProps.data.row.original?.waiting_on?.number"
            class="flex items-center bg-warning-50 rounded-full py-1 px-2 text-warning-600 gap-2 hover:bg-warning-100 !pointer-events-auto cursor-pointer"
            @click="router.push({ name: 'files-transmittals-detail', params: { asset_id: route.params.asset_id, transmittal_uid: slotProps.data.row.original?.waiting_on?.uid } })"
          >
            <IconHawkSlashCircleOne v-tippy="`Waiting on transmittal &quot;${slotProps.data.row.original?.waiting_on?.number}&quot; `" class="w-3 h-3" />
            <IconHawkChevronRight class="w-4 h-4" />
          </div>
        </div>
      </template>
      <template #due_date="{ data: { row: { original: { due_date } } } }">
        <div
          v-if="options.is_date_disabled"
          :class="[due_date && dayjs(due_date) <= dayjs() ? 'text-red-700' : 'text-gray-900']"
        >
          {{ options.due_date ? options.due_date : due_date ? $date(due_date, 'DATE_MED') : '-' }}
        </div>
        <DateTimeElement
          v-else
          :default="due_date"
          :options="{
            'min-date': dayjs().format('YYYY-MM-DD'),
          }"
          load-format="YYYYYY-MM-DDTHH:mm:ss.sssZ"
          :rules="['required']"
          name="due_date"
        />
      </template>
      <template #versions="{ data: { row: { original } } }">
        {{ original.versions ? original.versions.length : original.service_object ? 1 : 0 }}
      </template>
      <template #submitted_at="{ data: { row: { original: { submitted_at } } } }">
        <div class="text-gray-900">
          {{ submitted_at ? $date(submitted_at, 'DATE_MED') : '-' }}
        </div>
      </template>
      <template #comments_in="{ data: { row: { original: { comments_in } } } }">
        <div class="text-gray-900 break-all">
          {{ comments_in || '-' }}
        </div>
      </template>
      <template #comments_out="{ data: { row: { original } } }">
        <TextareaElement
          v-if="options.is_comments_enabled || checkPermission(original, 'can_review') || checkPermission(original, 'can_submit')"
          class="w-full"
          :default="original?.draft?.comments || original?.comments_out"
          name="comments"
          rows="1"
          :placeholder="$t('Comment here')"
          @click.stop
        />
        <div v-else class="text-gray-700 text-sm break-all">
          {{ original?.draft?.comments || original?.comments || original?.comments_out || '-' }}
        </div>
      </template>
      <template #status="{ data: { row: { original } } }">
        <div v-if="is_public_page">
          <hawk-badge v-if="form$.data.documents[original.uid]?.file || !original.actions.can_submit" color="green">
            {{ $t('Uploaded') }}
          </hawk-badge>
          <hawk-badge v-else color="yellow">
            {{ $t('Waiting for submission') }}
          </hawk-badge>
        </div>
        <div v-else>
          <TransmittalReviewStatus :document="original" :document_status_map="document_status_map" />
        </div>
      </template>
      <template #locked="{ data: { row: { original } } }">
        <div v-if="original.locked" class="pointer-events-auto">
          <IconHawkLockOne class="text-gray-500" />
        </div>
        <span v-else />
      </template>
      <template #delete="delete_row">
        <hawk-button v-if="canDelete" type="text" @click="emits('delete', delete_row.data.row.original.uid)">
          <IconHawkTrashOne />
        </hawk-button>
      </template>
      <template #public_uploads="{ data: { row: { original } } }">
        <FileElement
          v-if="original?.actions?.can_submit"
          name="file"
          class="w-full -mt-2"
          :presets="['hawk_file_element']"
          :can-select="true"
        >
          <template #upload-trigger>
            Upload
          </template>
        </FileElement>
      </template>
      <template v-for="(custom_field) in custom_fields" #[custom_field.uid]="slotProps" :key="custom_field.uid">
        <hawk-custom-field class="text-sm" :field="custom_field" :value="slotProps.data.row.original?.[custom_field.uid]" />
      </template>
      <template #context_menu="{ data: { row: { original } } }">
        <hawk-menu v-if="dropdown_items(original).length > 0" :items="dropdown_items(original)" additional_dropdown_classes="w-[200px]" position="bottom-left" @click.stop>
          <template #trigger>
            <IconHawkDotsVertical class="h-4 w-4" />
          </template>
        </hawk-menu>
      </template>
    </hawk-table>
    <hawk-button v-if="options.can_add_documents" type="link" @click="openBrowsePopup">
      <icon-hawk-plus />
      {{ $t('Add documents') }}
    </hawk-button>
    <div v-if="state.is_fetching_annotations" class="fixed z-50 right-6 bottom-10">
      <div
        class="bg-gray-800 px-4 text-white shadow-lg rounded-lg w-[400px] flex items-center justify-between text-sm"
        :class="state.is_fetching_annotations ? 'py-2.5' : 'py-4'"
      >
        <div class="flex gap-4 items-center">
          <div v-if="state.is_fetching_annotations" class="h-6 w-6 grid place-items-center ">
            <HawkLoader container_class="m-0" :height="5" :width="5" />
          </div>
          <HawkIconExportTick v-else />
          <div>
            <div class="font-semibold">
              <div>
                {{ $t('Fetching annotations for PDF files') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
